/**
 * Sidebar Component
 *
 * A comprehensive, reusable sidebar navigation component with support for:
 * - Collapsible mobile/desktop behavior
 * - Nested navigation items (for accordion functionality)
 * - Icons, badges, and custom styling
 * - Accessibility features
 * - Responsive design
 */

import React, { useState, useEffect, useRef } from "react";
import { NavLink, useLocation } from "react-router-dom";
import {
  ChevronDownIcon,
  ChevronRightIcon,
  XMarkIcon,
  Bars3Icon,
} from "@heroicons/react/24/outline";
import { ComponentErrorBoundary } from "../ErrorBoundary";

/**
 * Main Sidebar Component
 */
export default function Sidebar({
  isOpen = false,
  onToggle,
  onClose,
  className = "",
  width = "w-64",
  brand,
  navigation = [],
  footer,
  collapsible = true,
  overlay = true,
  children,
  ...props
}) {
  const location = useLocation();
  const sidebarRef = useRef(null);

  // Handle click outside to close sidebar on mobile
  useEffect(() => {
    function handleClickOutside(event) {
      if (
        sidebarRef.current &&
        !sidebarRef.current.contains(event.target) &&
        isOpen &&
        window.innerWidth < 1024 // Only on mobile
      ) {
        onClose?.();
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen, onClose]);

  // Handle escape key
  useEffect(() => {
    function handleEscape(event) {
      if (event.key === "Escape" && isOpen) {
        onClose?.();
      }
    }

    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [isOpen, onClose]);

  return (
    <ComponentErrorBoundary componentName="Sidebar">
      {/* Mobile overlay */}
      {overlay && isOpen && (
        <div
          className="fixed inset-0 z-30 bg-gray-600 bg-opacity-75 lg:hidden transition-opacity"
          onClick={onClose}
          aria-hidden="true"
        />
      )}

      {/* Sidebar */}
      <div
        ref={sidebarRef}
        className={`
          ${width} bg-white shadow-lg transition-transform duration-300 ease-in-out
          fixed inset-y-0 left-0 z-40 transform
          ${isOpen ? "translate-x-0" : "-translate-x-full"}
          lg:relative lg:inset-auto lg:translate-x-0
          ${className}
        `}
        {...props}
      >
        <div className="flex flex-col h-full">
          {/* Brand/Header */}
          {brand && (
            <div className="flex items-center justify-between h-16 px-6 border-b border-gray-200 flex-shrink-0">
              {typeof brand === "function" ? brand() : brand}

              {/* Close button for mobile */}
              {collapsible && (
                <button
                  className="lg:hidden p-1 rounded-md hover:bg-gray-100 transition-colors"
                  onClick={onClose}
                  aria-label="Close sidebar"
                >
                  <XMarkIcon className="h-6 w-6 text-gray-600" />
                </button>
              )}
            </div>
          )}

          {/* Navigation */}
          <nav className="flex-1 px-4 py-6 space-y-1 overflow-y-auto">
            {navigation.map((item, index) => (
              <SidebarItem
                key={item.key || item.href || index}
                item={item}
                currentPath={location.pathname}
              />
            ))}
            {children}
          </nav>

          {/* Footer */}
          {footer && (
            <div className="px-4 py-4 border-t border-gray-200 flex-shrink-0">
              {typeof footer === "function" ? footer() : footer}
            </div>
          )}
        </div>
      </div>
    </ComponentErrorBoundary>
  );
}

/**
 * Individual Sidebar Navigation Item
 */
function SidebarItem({ item, currentPath, level = 0 }) {
  const [isExpanded, setIsExpanded] = useState(false);
  const hasChildren = item.children && item.children.length > 0;
  const isActive = item.href && currentPath === item.href;
  const isParentActive =
    hasChildren &&
    item.children.some((child) => child.href && currentPath === child.href);

  // Auto-expand if child is active
  useEffect(() => {
    if (isParentActive) {
      setIsExpanded(true);
    }
  }, [isParentActive]);

  const handleToggle = (e) => {
    if (hasChildren) {
      e.preventDefault();
      setIsExpanded(!isExpanded);
    }
  };

  const handleClick = (e) => {
    if (item.onClick) {
      item.onClick(e);
    }
  };

  // Base classes for nav items
  const baseClasses = `
    flex items-center justify-between w-full px-3 py-2 text-sm font-medium rounded-lg 
    transition-all duration-200 group relative
  `;

  const levelIndent = level > 0 ? `ml-${level * 4}` : "";

  // Active/inactive states
  const stateClasses = isActive
    ? "bg-blue-50 text-blue-700 border-l-4 border-blue-600"
    : isParentActive
      ? "bg-blue-25 text-blue-600"
      : "text-gray-600 hover:bg-gray-50 hover:text-gray-900";

  // If item is disabled
  if (item.disabled) {
    return (
      <div
        className={`${baseClasses} ${levelIndent} opacity-50 cursor-not-allowed text-gray-400`}
      >
        <div className="flex items-center space-x-3">
          {item.icon && <ItemIcon icon={item.icon} />}
          <span>{item.label || item.name}</span>
          {item.badge && <ItemBadge badge={item.badge} />}
        </div>
      </div>
    );
  }

  // Render item
  const ItemContent = () => (
    <>
      <div className="flex items-center space-x-3 flex-1 min-w-0">
        {item.icon && <ItemIcon icon={item.icon} />}
        <span className="truncate">{item.label || item.name}</span>
        {item.badge && <ItemBadge badge={item.badge} />}
      </div>

      {hasChildren && (
        <div className="flex-shrink-0 ml-2">
          {isExpanded ? (
            <ChevronDownIcon className="h-4 w-4 text-gray-500 transition-transform" />
          ) : (
            <ChevronRightIcon className="h-4 w-4 text-gray-500 transition-transform" />
          )}
        </div>
      )}
    </>
  );

  return (
    <div>
      {/* Main item */}
      {item.href && !hasChildren ? (
        <NavLink
          to={item.href}
          className={`${baseClasses} ${levelIndent} ${stateClasses}`}
          onClick={handleClick}
          title={item.description}
        >
          <ItemContent />
        </NavLink>
      ) : (
        <button
          className={`${baseClasses} ${levelIndent} ${stateClasses}`}
          onClick={hasChildren ? handleToggle : handleClick}
          title={item.description}
          aria-expanded={hasChildren ? isExpanded : undefined}
        >
          <ItemContent />
        </button>
      )}

      {/* Children (accordion content) */}
      {hasChildren && (
        <div
          className={`
            overflow-hidden transition-all duration-200 ease-in-out
            ${isExpanded ? "max-h-96 opacity-100" : "max-h-0 opacity-0"}
          `}
        >
          <div className="py-1 space-y-1">
            {item.children.map((child, index) => (
              <SidebarItem
                key={child.key || child.href || index}
                item={child}
                currentPath={currentPath}
                level={level + 1}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Icon Component for Sidebar Items
 */
function ItemIcon({ icon }) {
  if (typeof icon === "string") {
    // Emoji or text icon
    return <span className="text-lg flex-shrink-0">{icon}</span>;
  }

  if (React.isValidElement(icon)) {
    // React component
    return <div className="h-5 w-5 flex-shrink-0">{icon}</div>;
  }

  // Heroicon or other component
  const IconComponent = icon;
  return <IconComponent className="h-5 w-5 flex-shrink-0" />;
}

/**
 * Badge Component for Sidebar Items
 */
function ItemBadge({ badge }) {
  if (typeof badge === "string" || typeof badge === "number") {
    return (
      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
        {badge}
      </span>
    );
  }

  if (badge.count !== undefined) {
    const { count, color = "gray", variant = "subtle" } = badge;

    const colorClasses = {
      gray:
        variant === "solid"
          ? "bg-gray-600 text-white"
          : "bg-gray-100 text-gray-800",
      red:
        variant === "solid"
          ? "bg-red-600 text-white"
          : "bg-red-100 text-red-800",
      blue:
        variant === "solid"
          ? "bg-blue-600 text-white"
          : "bg-blue-100 text-blue-800",
      green:
        variant === "solid"
          ? "bg-green-600 text-white"
          : "bg-green-100 text-green-800",
      yellow:
        variant === "solid"
          ? "bg-yellow-600 text-white"
          : "bg-yellow-100 text-yellow-800",
      purple:
        variant === "solid"
          ? "bg-purple-600 text-white"
          : "bg-purple-100 text-purple-800",
    };

    return (
      <span
        className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${colorClasses[color]}`}
      >
        {count}
      </span>
    );
  }

  return badge;
}

/**
 * Sidebar Toggle Button (for external use)
 */
export function SidebarToggle({ onToggle, isOpen, className = "", ...props }) {
  return (
    <button
      className={`p-2 rounded-md hover:bg-gray-100 transition-colors ${className}`}
      onClick={onToggle}
      aria-label={isOpen ? "Close sidebar" : "Open sidebar"}
      {...props}
    >
      <Bars3Icon className="h-6 w-6 text-gray-600" />
    </button>
  );
}

/**
 * Default Brand Component
 */
export function DefaultBrand({ title = "RE Platform", icon = "🏢", version }) {
  return (
    <div className="flex items-center space-x-3">
      {/* <span className="text-2xl">{icon}</span>
      <div className="flex flex-col">
        <span className="text-xl font-bold text-gray-900">{title}</span>
        {version && <span className="text-xs text-gray-500">{version}</span>}
      </div> */}
    </div>
  );
}

/**
 * Navigation Section Divider
 */
export function SidebarDivider({ label, className = "" }) {
  return (
    <div className={`py-2 ${className}`}>
      {label && (
        <div className="px-3 mb-2">
          <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider">
            {label}
          </p>
        </div>
      )}
      <hr className="border-gray-200" />
    </div>
  );
}

/**
 * User Profile Section (for sidebar footer)
 */
export function SidebarUserProfile({
  user,
  showStatus = true,
  onProfileClick,
  onSignOut,
}) {
  if (!user) return null;

  return (
    <div className="px-2">
      <div className="flex items-center space-x-3 p-2 rounded-lg hover:bg-gray-50 transition-colors">
        {/* Avatar */}
        <div className="flex-shrink-0">
          {user.avatar ? (
            <img
              className="h-8 w-8 rounded-full"
              src={user.avatar}
              alt={user.name}
            />
          ) : (
            <div className="h-8 w-8 bg-blue-600 rounded-full flex items-center justify-center">
              <span className="text-white text-sm font-medium">
                {user.name?.charAt(0) || "U"}
              </span>
            </div>
          )}
        </div>

        {/* User info */}
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-gray-900 truncate">
            {user.name || "User"}
          </p>
          {showStatus && user.email && (
            <p className="text-xs text-gray-500 truncate">{user.email}</p>
          )}
        </div>

        {/* Actions */}
        <div className="flex-shrink-0">
          <button
            className="p-1 text-gray-400 hover:text-gray-600 transition-colors"
            onClick={onProfileClick}
            title="Profile settings"
          >
            <span className="text-sm">⚙️</span>
          </button>
        </div>
      </div>

      {onSignOut && (
        <button
          className="w-full mt-2 px-3 py-2 text-left text-sm text-gray-600 hover:bg-gray-50 rounded-lg transition-colors"
          onClick={onSignOut}
        >
          Sign out
        </button>
      )}
    </div>
  );
}
